/**
 * Product Updates is a lightweight changelog surface, distinct from the
 * Resources library. Resources are evergreen, source-backed guides;
 * product updates are dated announcements about what shipped in the
 * PinkSpider product itself. Kept as a separate content model so the two
 * never get mixed together in the same feed or nav treatment.
 */

export type ProductUpdateStatus = "draft" | "published";

export type ProductUpdateCategory = "New" | "Integration" | "Improvement";

export interface ProductUpdateLink {
  href: string;
  label: string;
  description?: string;
}

export interface ProductUpdateSection {
  id: string;
  heading: string;
  paragraphs: string[];
  bullets?: string[];
  orderedBullets?: boolean;
  cards?: { title: string; body: string }[];
  flagAnatomy?: {
    severityLabel: string;
    rows: { label: string; value: string }[];
  };
}

interface ProductUpdateCommonFields {
  slug: string;
  status: ProductUpdateStatus;
  title: string;
  summary: string;
  category: ProductUpdateCategory;
  heroImage: string;
  author: string;
  seo: {
    title: string;
    description: string;
  };
  directAnswer: string;
  highlights: string[];
  externalLink?: {
    href: string;
    label: string;
    icon?: "figma";
  };
  sections: ProductUpdateSection[];
  availability: {
    plans: string;
    platforms: string;
    rollout: string;
  };
  relatedProduct: ProductUpdateLink;
  relatedLinks: ProductUpdateLink[];
  finalCta: {
    title: string;
    body: string;
    href: string;
    label: string;
  };
}

export type PublishedProductUpdate = ProductUpdateCommonFields & {
  status: "published";
  publishedAt: string;
};

export type DraftProductUpdate = ProductUpdateCommonFields & {
  status: "draft";
  publishedAt?: string;
};

export type ProductUpdate = PublishedProductUpdate | DraftProductUpdate;

export interface ProductUpdateRoute {
  path: string;
  status: 200;
  indexation: "index";
  title: string;
  description: string;
  changefreq: "monthly";
  priority: string;
  content: {
    h1: string;
    paragraphs: string[];
    links: ProductUpdateLink[];
  };
  productUpdate: PublishedProductUpdate;
}

export const PRODUCT_UPDATE_CATEGORY_LABELS: Record<ProductUpdateCategory, string> = {
  New: "New",
  Integration: "Integration",
  Improvement: "Improvement",
};

export const PRODUCT_UPDATES: ProductUpdate[] = [
  {
    slug: "figma-plugin",
    status: "published",
    publishedAt: "2026-09-17",
    title: "The PinkSpider Figma Plugin: Catch Compliance Issues Before a Design Ships",
    summary:
      "Run your brand's Marketing Review on social, campaign, and web graphics right inside Figma, before they ever reach a reviewer's desk.",
    category: "New",
    heroImage: "/product-update-figma-plugin-hero.webp",
    author: "PinkSpider Product Team",
    seo: {
      title: "PinkSpider Figma Plugin | Compliance Review Inside Figma",
      description:
        "The PinkSpider Figma plugin flags disclosure, claims, and brand guideline issues on marketing creative directly inside your Figma file, before it ships.",
    },
    directAnswer:
      "The PinkSpider Figma plugin sends your selected frame straight into your brand's Marketing Review pipeline, without leaving Figma. You get the same AI-driven analysis PinkSpider runs on every submitted asset: brand alignment, regulatory compliance, industry standards, audience fit, and content safety, so issues surface while the design is still editable, not after it ships.",
    highlights: [
      "Reviews a selected frame or an entire page without leaving Figma",
      "Flags disclosure, claims, and brand guideline issues with inline comments pinned to the exact layer",
      "Uses the same Compliance Intelligence standard already applied to creator and marketing review",
      "Available today as part of Marketing Review on paid PinkSpider plans with a connected Figma workspace",
    ],
    externalLink: {
      href: "https://www.figma.com",
      label: "Find PinkSpider on the Figma Community",
      icon: "figma",
    },
    sections: [
      {
        id: "why-we-built-it",
        heading: "Why we built it",
        paragraphs: [
          "Most compliance review happens too late: after a designer hands off a file, after it's dropped into a submission form, sometimes after it's already gone live. By the time an issue is flagged, fixing it means reopening the file, re-exporting, and resubmitting; often after the campaign timeline has already moved on. We built the Figma plugin so a designer can check a frame against the brand's actual compliance standard the moment it's ready, and catch issues before the file ever leaves Figma.",
        ],
      },
      {
        id: "how-it-works",
        heading: "How it works",
        paragraphs: [],
        bullets: [
          "Select a frame in Figma and open the PinkSpider plugin panel.",
          "The plugin exports the frame and sends it into your brand's Marketing Review, the same analysis pipeline used for every other content submission.",
          "Once the review completes, the plugin shows the overall score, risk level, and top flags directly in the panel.",
          "Flags are also posted back as pinpointed comments on the exact elements they reference, so context stays attached to the design, not a separate report.",
        ],
      },
      {
        id: "what-it-catches",
        heading: "What it catches",
        paragraphs: [
          "The plugin evaluates your frame across the same five dimensions as every Marketing Review:",
        ],
        cards: [
          { title: "Brand Alignment", body: "Voice, tone, and adherence to your brand guidelines" },
          { title: "Regulatory Compliance", body: "Disclosure and claims-related risk based on your compliance playbook" },
          { title: "Industry Standards", body: "Rules specific to your industry's regulatory environment" },
          { title: "Audience Alignment", body: "Fit with the intended audience and channel" },
          { title: "Content Safety", body: "Prohibited or high-risk content" },
        ],
      },
      {
        id: "each-flag-includes",
        heading: "What each flag includes",
        paragraphs: [
          "Every flag is generated from your brand's own guidelines and knowledge library, not a generic checklist, and always comes back in the same shape.",
        ],
        flagAnatomy: {
          severityLabel: "Higher priority",
          rows: [
            { label: "Severity", value: "How urgent it is, from low to critical, so the highest-risk issues get fixed first." },
            { label: "Explanation", value: "A plain-language description of why it's a problem, tied to your brand's guideline or compliance rule." },
            { label: "Recommended fix", value: "A specific suggested change, not just a flag, so a designer can act on it immediately." },
          ],
        },
      },
      {
        id: "getting-started",
        heading: "Getting started",
        paragraphs: [
          "The plugin is available now as part of Marketing Review, for teams on a paid PinkSpider plan with a connected Figma workspace.",
        ],
        orderedBullets: true,
        bullets: [
          "Install \"PinkSpider \u00b7 Marketing Review\" from the Figma Community",
          "Sign in with your PinkSpider workspace credentials when prompted",
          "Open the file you want reviewed and select a frame or page",
          "Run \"Send to Review\" from the plugin panel and read the pinned comments",
        ],
      },
      {
        id: "whats-next",
        heading: "Where it fits in your workflow",
        paragraphs: [
          "The plugin gives you a first read on a frame while it's still in Figma, it isn't a replacement for your team's full review process. Every plugin review lands in the same Marketing Review dashboard as your other submissions, so reviewers can see the full history, add context, and make the final call before anything ships. Use the plugin to catch obvious issues early; use Marketing Review for the sign-off.",
        ],
      },
    ],
    availability: {
      plans: "Included in the Marketing Review product on paid PinkSpider plans",
      platforms: "Figma (Community plugin), desktop and browser",
      rollout: "Available now to all eligible workspaces",
    },
    relatedProduct: {
      href: "/solutions/marketing-review",
      label: "See Marketing Review",
      description: "The PinkSpider product this plugin extends into the design stage.",
    },
    relatedLinks: [
      {
        href: "/platform/compliance-intelligence",
        label: "How Compliance Intelligence builds your review standard",
      },
      {
        href: "/platform/integrations",
        label: "See all PinkSpider integrations",
      },
    ],
    finalCta: {
      title: "Bring compliance review into your design workflow",
      body: "See how the Figma plugin fits alongside the rest of your PinkSpider review and monitoring setup.",
      href: "/demo",
      label: "Request a PinkSpider demo",
    },
  },
];

export const PUBLISHED_PRODUCT_UPDATES = PRODUCT_UPDATES.filter(
  (update): update is PublishedProductUpdate => update.status === "published",
).sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));

export function getProductUpdateBySlug(slug: string): PublishedProductUpdate | undefined {
  return PUBLISHED_PRODUCT_UPDATES.find((update) => update.slug === slug);
}

export function getProductUpdateRoute(path: string): ProductUpdateRoute | undefined {
  if (!path.startsWith("/product-updates/")) return undefined;
  const slug = path.slice("/product-updates/".length);
  const update = getProductUpdateBySlug(slug);
  if (!update || path !== `/product-updates/${update.slug}`) return undefined;

  return {
    path,
    status: 200,
    indexation: "index",
    title: update.seo.title,
    description: update.seo.description,
    changefreq: "monthly",
    priority: "0.5",
    content: {
      h1: update.title,
      paragraphs: [update.directAnswer],
      links: [update.relatedProduct, ...update.relatedLinks, update.finalCta],
    },
    productUpdate: update,
  };
}

export const PUBLISHED_PRODUCT_UPDATE_ROUTES: ProductUpdateRoute[] = PUBLISHED_PRODUCT_UPDATES.map(
  (update) => getProductUpdateRoute(`/product-updates/${update.slug}`)!,
);
